import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { useEndpoints } from "../../../api/ApiContext";
import type { DisputeList, PartyType, StatementDispute } from "../../../api/types";
import { useSession } from "../../../auth/AuthProvider";
import { homeFor } from "../../../auth/pages";
import { CursorListView } from "../../../components/CursorListView";
import { Banner, Card, EmptyState, Money, PageHeader, QueryView, StatusPill, Table } from "../../../components/ui";
import { formatLocal } from "../../../lib/datetime";
import { useCursorList } from "../../../lib/useCursorList";
import { disputesPath, disputeStatus, formatBytes, lifecycle, MEDIA_LABEL, resolutionText, SCAN_STATE } from "./disputeCopy";

// Disputes list and detail (G-28 advertiser billing disputes, G-43
// publisher revenue-share disputes; UX-DESIGN.md §2.2, §3.6). GET
// /v1/disputes and /v1/disputes/{id} (disputes.go:79-82, the SQL's
// statement_line_dispute_json(false), 0063:648-705). Filing starts from a
// statement line (StatementsPages.tsx), because a dispute is always about
// one line of one issued statement.

function HowItWorks({ partyType }: { partyType: PartyType }) {
  return (
    <Card title="How disputes work" level={3}>
      <ul className="stack-sm small">
        <li>
          You dispute one line of an issued statement, from that statement's page. Each line shows its deadline: 30 days after the statement was
          issued. After that the line can't be disputed.
        </li>
        {partyType === "advertiser" ? (
          <li>
            Only billed deliveries can be disputed. While the dispute is open, that delivery isn't finalized. If it's upheld, the charge comes back
            to your wallet and the statement gets an amendment.
          </li>
        ) : (
          <li>
            Any line on your statement can be disputed. If a dispute about a delivery is upheld, a credit is posted against that delivery and you
            can include it in your next payout. A line that isn't tied to one delivery (a payout, for example) can't be credited automatically: an
            upheld dispute on it is resolved as a manual follow-up by VADS support.
          </li>
        )}
        <li>A line can be disputed once. A VADS reviewer decides; larger amounts need two reviewers.</li>
      </ul>
    </Card>
  );
}

export function DisputesPage() {
  const ep = useEndpoints();
  const session = useSession();
  const home = homeFor(session.partyType);
  const advertiser = session.partyType === "advertiser";
  const list = useCursorList<DisputeList, StatementDispute>(["disputes"], (cursor) => ep.disputes.list(cursor), (p) => p.disputes);

  return (
    <div className="stack-lg">
      <PageHeader
        title={advertiser ? "Billing disputes" : "Disputes"}
        description="Disputes are filed against a specific line on a specific statement, within that line's dispute window."
      />
      <Card title="Your disputes">
        <CursorListView
          list={list}
          loadingLabel="Loading disputes"
          empty={
            <EmptyState icon="scale" title="No disputes">
              To dispute a charge or earning, open the <Link to={`${home}/statements`}>statement</Link> it's on and choose "Dispute this line".
            </EmptyState>
          }
        >
          {(rows) => (
            <Table
              caption="Your disputes, newest first"
              captionHidden
              rows={rows}
              rowKey={(d) => d.dispute_id}
              columns={[
                { key: "filed", header: "Filed", cell: (d) => <Link to={disputesPath(home, d.dispute_id)}>{formatLocal(d.filed_at)}</Link> },
                {
                  key: "line",
                  header: "Statement line",
                  cell: (d) => (
                    <span>
                      #{d.line.line_no} · {d.line.description}
                    </span>
                  ),
                },
                { key: "amount", header: "Line amount", cell: (d) => <Money value={d.line.amount} currency={d.line.currency} />, numeric: true },
                { key: "status", header: "Status", cell: (d) => <StatusPill tone={disputeStatus(d).tone}>{disputeStatus(d).label}</StatusPill> },
              ]}
            />
          )}
        </CursorListView>
      </Card>
      <HowItWorks partyType={session.partyType} />
    </div>
  );
}

export function DisputeDetailPage() {
  const { disputeId = "" } = useParams();
  const ep = useEndpoints();
  const session = useSession();
  const home = homeFor(session.partyType);
  const q = useQuery({ queryKey: ["dispute", disputeId], queryFn: () => ep.disputes.get(disputeId) });

  return (
    <div className="stack-lg">
      <PageHeader title="Dispute" back={{ to: disputesPath(home), label: "All disputes" }} />
      <QueryView query={q} loadingLabel="Loading dispute" lines={6}>
        {(d) => {
          const status = disputeStatus(d);
          return (
            <div className="stack-lg">
              <Card
                title={`Line ${d.line.line_no}: ${d.line.description}`}
                subtitle={`Filed ${formatLocal(d.filed_at)}`}
                actions={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
              >
                <div className="stack">
                  <ol className="steps" aria-label="Dispute progress">
                    {lifecycle(d).map((s) => (
                      <li key={s.label} className={`step${s.done ? " step-done" : ""}`} aria-current={s.current ? "step" : undefined}>
                        {s.label}
                      </li>
                    ))}
                  </ol>
                  <dl className="definition-list">
                    <dt>Statement</dt>
                    <dd>
                      <Link to={`${home}/statements/${d.statement_id}`}>Open the statement</Link>
                    </dd>
                    <dt>Line amount</dt>
                    <dd>
                      <Money value={d.line.amount} currency={d.line.currency} />
                    </dd>
                    <dt>Dispute window</dt>
                    <dd>until {formatLocal(d.dispute_eligible_until)}</dd>
                    <dt>Your reason</dt>
                    <dd style={{ whiteSpace: "pre-wrap" }}>{d.reason}</dd>
                  </dl>
                </div>
              </Card>

              <Card title="Resolution">
                {d.state === "open" ? (
                  <p className="small">
                    A VADS reviewer is looking at this dispute. {session.partyType === "advertiser" ? "Until it's decided, this delivery isn't finalized." : ""}
                  </p>
                ) : (
                  <div className="stack">
                    {d.resolution ? (
                      <Banner tone={d.outcome === "upheld" ? "success" : "neutral"} title={d.outcome === "upheld" ? "Upheld" : "Denied"}>
                        {resolutionText(d.resolution, session.partyType)}
                      </Banner>
                    ) : null}
                    <dl className="definition-list">
                      {d.decided_at ? (
                        <>
                          <dt>Decided</dt>
                          <dd>{formatLocal(d.decided_at)}</dd>
                        </>
                      ) : null}
                      {d.resolution_note ? (
                        <>
                          <dt>Reviewer's note</dt>
                          <dd style={{ whiteSpace: "pre-wrap" }}>{d.resolution_note}</dd>
                        </>
                      ) : null}
                      {d.amount && d.resolution !== "denied" ? (
                        <>
                          <dt>{d.resolution === "credited" ? "Credited" : "Amount reversed"}</dt>
                          <dd>
                            <Money value={d.amount} currency={d.currency} />
                          </dd>
                        </>
                      ) : null}
                      {d.reversal_journal_entry_id ? (
                        <>
                          <dt>{d.resolution === "credited" ? "Credit posting" : "Reversal posting"}</dt>
                          <dd>
                            <code className="xs">{d.reversal_journal_entry_id}</code>
                          </dd>
                        </>
                      ) : null}
                      {d.outcome === "upheld" && (d.resolution === "reversed" || d.resolution === "credited") ? (
                        <>
                          <dt>Statement amendment</dt>
                          <dd>
                            {d.amendment_id ? (
                              <Link to={`${home}/statements/${d.statement_id}#amendment-${d.amendment_id}`}>See the amendment</Link>
                            ) : (
                              <span className="muted">Not issued yet. Amendments are issued by the statement job; it'll be linked here once it is.</span>
                            )}
                          </dd>
                        </>
                      ) : null}
                    </dl>
                  </div>
                )}
              </Card>

              <Card title="Evidence" subtitle="File names aren't stored, so files are listed by type and size.">
                {d.evidence.length === 0 ? (
                  <p className="muted small">No evidence was attached.</p>
                ) : (
                  <Table
                    caption="Evidence attached to this dispute"
                    captionHidden
                    rows={d.evidence}
                    rowKey={(e) => e.evidence_id}
                    columns={[
                      { key: "type", header: "Type", cell: (e) => MEDIA_LABEL[e.media_type] ?? e.media_type },
                      { key: "size", header: "Size", cell: (e) => formatBytes(e.byte_size), numeric: true },
                      { key: "uploaded", header: "Uploaded", cell: (e) => formatLocal(e.uploaded_at) },
                      { key: "scan", header: "Check", cell: (e) => <StatusPill tone={SCAN_STATE[e.scan_state].tone}>{SCAN_STATE[e.scan_state].label}</StatusPill> },
                      { key: "keep", header: "Kept until", cell: (e) => formatLocal(e.retain_until) },
                    ]}
                  />
                )}
              </Card>
            </div>
          );
        }}
      </QueryView>
    </div>
  );
}
